/**
 * Pure rendering functions for 2D canvas
 * No React, just canvas API calls
 */

import { Vec2, worldToScreen } from './coordinates';
import type {
  StageObject,
  StageObjectType,
  ObjectVariant,
  ShootingPosition,
  Wall,
  Annotation,
  CanvasConfig,
} from '@/stage/types';
import {
  CHROME,
  WALL,
  POSITION,
  PAPER,
  NO_SHOOT,
  STEEL,
  POPPER,
  BARREL,
  TABLE as TABLE_COLORS,
  CONE as CONE_COLORS,
  FURNITURE,
  CAR,
  FAULT_LINE,
  HARD_COVER,
  SOFT_COVER,
  PLATE,
  MOVING,
  FALLBACK,
  SHOOTER,
  ANNOTATION,
  LABEL,
} from './theme';

/**
 * Render the stage boundary — the playable area defined by canvas.width_m /
 * height_m. Drawn under the grid so targets and walls sit on top of it, with
 * dimension labels on the top and left edges so the user can always see how
 * big their stage is.
 */
export function renderStageBounds(
  ctx: CanvasRenderingContext2D,
  canvas: CanvasConfig,
  zoom: number,
  panOffset: Vec2,
): void {
  ctx.save();

  const topLeft = worldToScreen({ x: 0, y: 0 }, zoom, panOffset);
  const bottomRight = worldToScreen(
    { x: canvas.width_m, y: canvas.height_m },
    zoom,
    panOffset,
  );
  const w = bottomRight.x - topLeft.x;
  const h = bottomRight.y - topLeft.y;

  // Soft fill to distinguish the stage area from the surrounding "off-stage"
  ctx.fillStyle = CHROME.stageFill;
  ctx.fillRect(topLeft.x, topLeft.y, w, h);

  // Outer boundary
  ctx.strokeStyle = CHROME.stageBorder;
  ctx.lineWidth = 2;
  ctx.strokeRect(topLeft.x, topLeft.y, w, h);

  // Dimension labels — "30.0 m" on top edge, "20.0 m" on left edge
  ctx.fillStyle = CHROME.stageLabel;
  ctx.font = 'bold 12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(`${canvas.width_m.toFixed(1)} m`, topLeft.x + w / 2, topLeft.y - 6);

  ctx.save();
  ctx.translate(topLeft.x - 6, topLeft.y + h / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(`${canvas.height_m.toFixed(1)} m`, 0, 0);
  ctx.restore();

  // Dimension tick lines on the outside
  ctx.strokeStyle = CHROME.stageTicks;
  ctx.lineWidth = 1;
  const tick = 6;
  // Top edge
  ctx.beginPath();
  ctx.moveTo(topLeft.x, topLeft.y - tick);
  ctx.lineTo(topLeft.x, topLeft.y);
  ctx.moveTo(bottomRight.x, topLeft.y - tick);
  ctx.lineTo(bottomRight.x, topLeft.y);
  ctx.moveTo(topLeft.x, topLeft.y - tick / 2);
  ctx.lineTo(bottomRight.x, topLeft.y - tick / 2);
  // Left edge
  ctx.moveTo(topLeft.x - tick, topLeft.y);
  ctx.lineTo(topLeft.x, topLeft.y);
  ctx.moveTo(topLeft.x - tick, bottomRight.y);
  ctx.lineTo(topLeft.x, bottomRight.y);
  ctx.moveTo(topLeft.x - tick / 2, topLeft.y);
  ctx.lineTo(topLeft.x - tick / 2, bottomRight.y);
  ctx.stroke();

  ctx.restore();
}

/**
 * Render the grid overlay
 */
export function renderGrid(
  ctx: CanvasRenderingContext2D,
  canvas: CanvasConfig,
  zoom: number,
  panOffset: Vec2,
  viewWidth: number,
  viewHeight: number,
  gridSize: number
): void {
  ctx.save();

  const majorGridSize = gridSize * 5; // Major line every 5 units

  // Calculate visible grid range
  const startX = Math.floor(-panOffset.x / (gridSize * zoom)) * gridSize;
  const startY = Math.floor(-panOffset.y / (gridSize * zoom)) * gridSize;
  const endX = startX + viewWidth / zoom + gridSize;
  const endY = startY + viewHeight / zoom + gridSize;

  // Draw minor grid lines
  ctx.strokeStyle = CHROME.grid;
  ctx.lineWidth = 1 / zoom;

  // Vertical lines
  for (let x = startX; x < endX; x += gridSize) {
    if (x % majorGridSize !== 0) {
      ctx.beginPath();
      const screenX = x * zoom + panOffset.x;
      ctx.moveTo(screenX, 0);
      ctx.lineTo(screenX, viewHeight);
      ctx.stroke();
    }
  }

  // Horizontal lines
  for (let y = startY; y < endY; y += gridSize) {
    if (y % majorGridSize !== 0) {
      ctx.beginPath();
      const screenY = y * zoom + panOffset.y;
      ctx.moveTo(0, screenY);
      ctx.lineTo(viewWidth, screenY);
      ctx.stroke();
    }
  }

  // Draw major grid lines
  ctx.strokeStyle = CHROME.gridMajor;
  ctx.lineWidth = 2 / zoom;

  // Major vertical lines
  for (let x = startX; x < endX; x += majorGridSize) {
    ctx.beginPath();
    const screenX = x * zoom + panOffset.x;
    ctx.moveTo(screenX, 0);
    ctx.lineTo(screenX, viewHeight);
    ctx.stroke();
  }

  // Major horizontal lines
  for (let y = startY; y < endY; y += majorGridSize) {
    ctx.beginPath();
    const screenY = y * zoom + panOffset.y;
    ctx.moveTo(0, screenY);
    ctx.lineTo(viewWidth, screenY);
    ctx.stroke();
  }

  // Draw grid labels on left and top edges
  ctx.fillStyle = CHROME.gridText;
  ctx.font = `${12}px sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';

  // X-axis labels (top)
  for (let x = startX; x < endX; x += majorGridSize) {
    if (x >= 0 && x <= canvas.width_m) {
      const screenX = x * zoom + panOffset.x;
      if (screenX > 0 && screenX < viewWidth) {
        ctx.fillText(`${x.toFixed(0)}m`, screenX - 2, 2);
      }
    }
  }

  // Y-axis labels (left)
  ctx.textAlign = 'right';
  for (let y = startY; y < endY; y += majorGridSize) {
    if (y >= 0 && y <= canvas.height_m) {
      const screenY = y * zoom + panOffset.y;
      if (screenY > 0 && screenY < viewHeight) {
        ctx.fillText(`${y.toFixed(0)}m`, 20, screenY - 10);
      }
    }
  }

  ctx.restore();
}

/**
 * Render a wall
 */
export function renderWall(
  ctx: CanvasRenderingContext2D,
  wall: Wall,
  zoom: number,
  panOffset: Vec2,
  isSelected: boolean
): void {
  ctx.save();

  if (wall.points.length < 2) {
    ctx.restore();
    return;
  }

  const points = wall.points.map((p) =>
    worldToScreen({ x: p.x, y: p.y }, zoom, panOffset)
  );

  ctx.strokeStyle = isSelected ? CHROME.selected : WALL.fill;
  ctx.lineWidth = 2;

  if (wall.type === 'mesh_fence') {
    // Dashed line for mesh fences
    ctx.setLineDash([5, 5]);
  }

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }

  ctx.stroke();

  // Fill wall area for port_wall
  if (wall.type === 'port_wall' && wall.has_port) {
    ctx.fillStyle = isSelected ? CHROME.selected : WALL.fill;
    ctx.globalAlpha = 0.3;
    ctx.fill();
    ctx.globalAlpha = 1.0;
  }

  ctx.restore();
}

/**
 * Render a shooting position
 */
export function renderPosition(
  ctx: CanvasRenderingContext2D,
  pos: ShootingPosition,
  zoom: number,
  panOffset: Vec2,
  isSelected: boolean
): void {
  ctx.save();

  const screenPos = worldToScreen(
    { x: pos.position.x, y: pos.position.y },
    zoom,
    panOffset
  );

  const screenWidth = pos.width_m * zoom;
  const screenDepth = pos.depth_m * zoom;

  // Draw rectangle
  ctx.fillStyle = POSITION.fill;
  ctx.globalAlpha = 0.3;
  ctx.fillRect(
    screenPos.x - screenWidth / 2,
    screenPos.y - screenDepth / 2,
    screenWidth,
    screenDepth
  );

  // Draw border
  ctx.globalAlpha = 1.0;
  ctx.strokeStyle = isSelected ? CHROME.selected : POSITION.border;
  ctx.lineWidth = 2;
  ctx.strokeRect(
    screenPos.x - screenWidth / 2,
    screenPos.y - screenDepth / 2,
    screenWidth,
    screenDepth
  );

  // Draw label or "S" for start positions
  ctx.fillStyle = CHROME.text;
  ctx.font = `bold ${14}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const label = pos.is_start ? 'S' : pos.label || pos.id.substring(0, 3);
  ctx.fillText(label, screenPos.x, screenPos.y);

  ctx.restore();
}

/**
 * Render a stage object
 */
export function renderObject(
  ctx: CanvasRenderingContext2D,
  obj: StageObject,
  zoom: number,
  panOffset: Vec2,
  isSelected: boolean,
  isHovered: boolean
): void {
  ctx.save();

  const screenPos = worldToScreen(
    { x: obj.position.x, y: obj.position.y },
    zoom,
    panOffset
  );

  ctx.translate(screenPos.x, screenPos.y);
  ctx.rotate((obj.rotation.z * Math.PI) / 180);

  // Apply highlight if hovered
  if (isHovered) {
    ctx.globalAlpha = 0.8;
  }

  const type = obj.type;

  // Paper targets — IDPA silhouette
  if (type === 'paper_target') {
    // IDPA variant is slightly taller and narrower than the IPSC classic used
    // as the default. Everything else shares the same silhouette geometry.
    const isIdpa = obj.variant === 'idpa';
    const bw = (isIdpa ? 0.432 : 0.457) * zoom;  // body width
    const bh = (isIdpa ? 0.610 : 0.584) * zoom;  // body height
    const hdw = 0.108 * zoom;  // head width
    const hdh = 0.178 * zoom;  // head height
    const c  = 0.076 * zoom;   // corner cut
    const totalH = bh + hdh;
    const hw = bw / 2;
    const hhw = hdw / 2;

    // Centre full silhouette at origin (head up = -Y)
    const top = -totalH / 2;
    const headBot = top + hdh;
    const bot = totalH / 2;

    // Fill silhouette
    ctx.fillStyle = PAPER.body;
    ctx.beginPath();
    ctx.moveTo(-hw + c, bot);
    ctx.lineTo( hw - c, bot);
    ctx.lineTo( hw, bot - c);
    ctx.lineTo( hw, headBot + c);
    ctx.lineTo( hw - c, headBot);
    ctx.lineTo( hhw, headBot);
    ctx.lineTo( hhw, top);
    ctx.lineTo(-hhw, top);
    ctx.lineTo(-hhw, headBot);
    ctx.lineTo(-hw + c, headBot);
    ctx.lineTo(-hw, headBot + c);
    ctx.lineTo(-hw, bot - c);
    ctx.closePath();
    ctx.fill();

    // Outline
    ctx.strokeStyle = PAPER.outline;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Down-0 chest circle
    const d0r = 0.102 * zoom;
    const d0y = headBot + bh * 0.48;
    ctx.beginPath();
    ctx.arc(0, d0y, d0r, 0, Math.PI * 2);
    ctx.stroke();

    // Down-0 head circle
    const hr = 0.051 * zoom;
    const hy = (top + headBot) / 2;
    ctx.beginPath();
    ctx.arc(0, hy, hr, 0, Math.PI * 2);
    ctx.stroke();

    // Down-1 inner octagon
    const d1hw = 0.152 * zoom;
    const d1h  = 0.457 * zoom;
    const d1c  = 0.051 * zoom;
    const d1Top = headBot + (bh - d1h) / 2;
    const d1Bot = d1Top + d1h;
    ctx.beginPath();
    ctx.moveTo(-d1hw + d1c, d1Bot);
    ctx.lineTo( d1hw - d1c, d1Bot);
    ctx.lineTo( d1hw, d1Bot - d1c);
    ctx.lineTo( d1hw, d1Top + d1c);
    ctx.lineTo( d1hw - d1c, d1Top);
    ctx.lineTo(-d1hw + d1c, d1Top);
    ctx.lineTo(-d1hw, d1Top + d1c);
    ctx.lineTo(-d1hw, d1Bot - d1c);
    ctx.closePath();
    ctx.stroke();
  }
  // Steel targets
  else if (type === 'steel_target') {
    const variant = obj.variant || 'round_8';
    const inchesToMeters = 0.0254;
    let diameter = 8 * inchesToMeters;

    if (variant === 'round_10') {
      diameter = 10 * inchesToMeters;
    } else if (variant === 'round_12') {
      diameter = 12 * inchesToMeters;
    }

    const radius = (diameter * zoom) / 2;
    ctx.fillStyle = STEEL.plate;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = STEEL.outline;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  // Poppers — circle head + narrow body + base
  else if (type === 'popper') {
    const headR   = 0.127 * zoom;
    const bodyW   = 0.076 * zoom;
    const bodyH   = 0.457 * zoom;
    const baseW   = 0.20  * zoom;
    const baseH   = 0.10  * zoom;
    const totalH  = baseH + bodyH + headR * 2;
    const baseBot = totalH / 2;
    const baseTop = baseBot - baseH;
    const bodyTop = baseTop - bodyH;
    const headCy  = bodyTop - headR;

    // Head circle
    ctx.fillStyle = POPPER.body;
    ctx.beginPath();
    ctx.arc(0, headCy, headR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = POPPER.bodyOutline;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Body (narrow rect)
    ctx.fillStyle = POPPER.body;
    ctx.fillRect(-bodyW / 2, bodyTop, bodyW, bodyH);
    ctx.strokeRect(-bodyW / 2, bodyTop, bodyW, bodyH);

    // Base
    ctx.fillStyle = POPPER.base;
    ctx.fillRect(-baseW / 2, baseTop, baseW, baseH);
    ctx.strokeRect(-baseW / 2, baseTop, baseW, baseH);
  }
  // No-shoots — IDPA silhouette with blue X
  else if (type === 'no_shoot') {
    const bw = 0.457 * zoom;
    const bh = 0.584 * zoom;
    const hdw = 0.108 * zoom;
    const hdh = 0.178 * zoom;
    const c  = 0.076 * zoom;
    const totalH = bh + hdh;
    const hw = bw / 2;
    const hhw = hdw / 2;

    const top = -totalH / 2;
    const headBot = top + hdh;
    const bot = totalH / 2;

    // Fill silhouette (same tan as paper target)
    ctx.fillStyle = PAPER.body;
    ctx.beginPath();
    ctx.moveTo(-hw + c, bot);
    ctx.lineTo( hw - c, bot);
    ctx.lineTo( hw, bot - c);
    ctx.lineTo( hw, headBot + c);
    ctx.lineTo( hw - c, headBot);
    ctx.lineTo( hhw, headBot);
    ctx.lineTo( hhw, top);
    ctx.lineTo(-hhw, top);
    ctx.lineTo(-hhw, headBot);
    ctx.lineTo(-hw + c, headBot);
    ctx.lineTo(-hw, headBot + c);
    ctx.lineTo(-hw, bot - c);
    ctx.closePath();
    ctx.fill();

    // Outline
    ctx.strokeStyle = PAPER.outline;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Blue no-shoot X cross over body area
    ctx.strokeStyle = NO_SHOOT.mark;
    ctx.lineWidth = 2;
    const xw = bw * 0.35;
    const xh = bh * 0.35;
    const xcy = headBot + bh * 0.48;
    ctx.beginPath();
    ctx.moveTo(-xw, xcy - xh);
    ctx.lineTo( xw, xcy + xh);
    ctx.moveTo(-xw, xcy + xh);
    ctx.lineTo( xw, xcy - xh);
    ctx.stroke();

    // Blue head circle
    const hr = 0.051 * zoom;
    const hy = (top + headBot) / 2;
    ctx.beginPath();
    ctx.arc(0, hy, hr, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Barrels — top-down. Stacked variants show concentric rings so the
  // stack count is readable at a glance.
  else if (type === 'barrel') {
    const variant = obj.variant || 'standard';
    const stack = variant === 'stack_3' ? 3 : variant === 'stack_2' ? 2 : 1;
    const radius = (0.58 * zoom) / 2;

    ctx.fillStyle = BARREL.body;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = BARREL.rim;
    ctx.lineWidth = 1;
    ctx.stroke();

    // One inset ring per stacked barrel (makes stack_2 / stack_3 distinct)
    for (let i = 0; i < stack; i++) {
      ctx.beginPath();
      ctx.arc(0, 0, radius * (0.78 - i * 0.15), 0, Math.PI * 2);
      ctx.stroke();
    }

    // Half barrel gets a horizontal cut line across the top to read as "half"
    if (variant === 'half') {
      ctx.strokeStyle = BARREL.rim;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(-radius, 0);
      ctx.lineTo(radius, 0);
      ctx.stroke();
    }

    // Bung hole marker
    ctx.fillStyle = BARREL.rim;
    ctx.beginPath();
    ctx.arc(radius * 0.45, 0, Math.max(1, radius * 0.08), 0, Math.PI * 2);
    ctx.fill();
  }
  // Tables — top-down: rectangle with visible legs at the corners. Low
  // variant has a smaller footprint.
  else if (type === 'table') {
    const low = obj.variant === 'low';
    const width = (low ? 1.0 : 1.5) * zoom;
    const depth = (low ? 0.6 : 0.8) * zoom;

    // Tabletop
    ctx.fillStyle = TABLE_COLORS.top;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(-width / 2, -depth / 2, width, depth);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = TABLE_COLORS.edge;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(-width / 2, -depth / 2, width, depth);

    // Inner inset frame — reads as a tabletop seam
    const inset = Math.max(2, 0.05 * zoom);
    ctx.strokeStyle = TABLE_COLORS.seam;
    ctx.lineWidth = 1;
    ctx.strokeRect(
      -width / 2 + inset, -depth / 2 + inset,
      width - inset * 2, depth - inset * 2,
    );

    // Corner leg markers
    const legSize = Math.max(3, 0.08 * zoom);
    ctx.fillStyle = TABLE_COLORS.leg;
    const legOffsets: Array<[number, number]> = [
      [-width / 2, -depth / 2],
      [ width / 2 - legSize, -depth / 2],
      [-width / 2,  depth / 2 - legSize],
      [ width / 2 - legSize,  depth / 2 - legSize],
    ];
    for (const [lx, ly] of legOffsets) {
      ctx.fillRect(lx, ly, legSize, legSize);
    }
  }
  // Chair — top-down: seat square with a backrest bar on the far edge
  else if (type === 'chair') {
    const w = 0.45 * zoom;
    const d = 0.45 * zoom;
    const backD = 0.07 * zoom;

    ctx.fillStyle = FURNITURE.wood;
    ctx.globalAlpha = 0.9;
    ctx.fillRect(-w / 2, -d / 2 + backD, w, d - backD);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = FURNITURE.outline;
    ctx.lineWidth = 1;
    ctx.strokeRect(-w / 2, -d / 2 + backD, w, d - backD);

    // Backrest bar
    ctx.fillStyle = FURNITURE.woodDark;
    ctx.fillRect(-w / 2, -d / 2, w, backD);
  }
  // Door — top-down: classic floor-plan symbol. Frame jambs, open door leaf
  // at ~80°, and the swing arc.
  else if (type === 'door') {
    const w = 1.0 * zoom;       // frame opening
    const jamb = 0.07 * zoom;
    const leafLen = w - jamb * 2;
    const openAngle = -(80 * Math.PI) / 180;

    // Jambs
    ctx.fillStyle = FURNITURE.woodDark;
    ctx.fillRect(-w / 2, -jamb / 2, jamb, jamb);
    ctx.fillRect(w / 2 - jamb, -jamb / 2, jamb, jamb);

    // Threshold line across the opening
    ctx.strokeStyle = FURNITURE.outline;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(-w / 2 + jamb, 0);
    ctx.lineTo(w / 2 - jamb, 0);
    ctx.stroke();
    ctx.setLineDash([]);

    // Swing arc from the left hinge
    const hingeX = -w / 2 + jamb;
    ctx.strokeStyle = FURNITURE.outline;
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.arc(hingeX, 0, leafLen, openAngle, 0);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Door leaf at the open angle
    ctx.strokeStyle = FURNITURE.wood;
    ctx.lineWidth = Math.max(2, 0.04 * zoom);
    ctx.beginPath();
    ctx.moveTo(hingeX, 0);
    ctx.lineTo(hingeX + Math.cos(openAngle) * leafLen, Math.sin(openAngle) * leafLen);
    ctx.stroke();
  }
  // Window — top-down: floor-plan symbol, frame with double pane lines
  else if (type === 'window') {
    const w = 1.2 * zoom;
    const d = 0.15 * zoom;
    const jamb = 0.07 * zoom;

    // Frame ends
    ctx.fillStyle = FURNITURE.woodDark;
    ctx.fillRect(-w / 2, -d / 2, jamb, d);
    ctx.fillRect(w / 2 - jamb, -d / 2, jamb, d);

    // Glass band
    ctx.fillStyle = FURNITURE.glass;
    ctx.globalAlpha = 0.5;
    ctx.fillRect(-w / 2 + jamb, -d / 2, w - jamb * 2, d);
    ctx.globalAlpha = 1;

    // Double pane lines
    ctx.strokeStyle = FURNITURE.outline;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-w / 2 + jamb, -d / 6);
    ctx.lineTo(w / 2 - jamb, -d / 6);
    ctx.moveTo(-w / 2 + jamb, d / 6);
    ctx.lineTo(w / 2 - jamb, d / 6);
    ctx.stroke();
  }
  // Bed — top-down: frame, mattress inset, pillow at the head end (-Y)
  else if (type === 'bed') {
    const w = 1.0 * zoom;
    const d = 2.0 * zoom;
    const inset = 0.06 * zoom;

    // Frame
    ctx.fillStyle = FURNITURE.woodDark;
    ctx.fillRect(-w / 2, -d / 2, w, d);

    // Mattress
    ctx.fillStyle = FURNITURE.mattress;
    ctx.fillRect(-w / 2 + inset, -d / 2 + inset, w - inset * 2, d - inset * 2);
    ctx.strokeStyle = FURNITURE.outline;
    ctx.lineWidth = 1;
    ctx.strokeRect(-w / 2 + inset, -d / 2 + inset, w - inset * 2, d - inset * 2);

    // Pillow
    ctx.fillStyle = FURNITURE.pillow;
    ctx.fillRect(-w / 2 + inset * 2, -d / 2 + inset * 2, w - inset * 4, 0.35 * zoom);
    ctx.strokeRect(-w / 2 + inset * 2, -d / 2 + inset * 2, w - inset * 4, 0.35 * zoom);

    // Blanket fold line
    ctx.beginPath();
    ctx.moveTo(-w / 2 + inset, -d / 2 + 0.6 * zoom);
    ctx.lineTo(w / 2 - inset, -d / 2 + 0.6 * zoom);
    ctx.stroke();
  }
  // Couch — top-down: seat with backrest along the far edge and two armrests
  else if (type === 'couch') {
    const w = 1.8 * zoom;
    const d = 0.85 * zoom;
    const backD = 0.2 * zoom;
    const armW = 0.15 * zoom;

    // Seat
    ctx.fillStyle = FURNITURE.fabric;
    ctx.globalAlpha = 0.9;
    ctx.fillRect(-w / 2, -d / 2, w, d);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = FURNITURE.outline;
    ctx.lineWidth = 1;
    ctx.strokeRect(-w / 2, -d / 2, w, d);

    // Backrest + armrests
    ctx.fillStyle = FURNITURE.fabricDark;
    ctx.fillRect(-w / 2, -d / 2, w, backD);
    ctx.fillRect(-w / 2, -d / 2, armW, d);
    ctx.fillRect(w / 2 - armW, -d / 2, armW, d);

    // Cushion split lines
    ctx.beginPath();
    ctx.moveTo(-w / 6, -d / 2 + backD);
    ctx.lineTo(-w / 6, d / 2);
    ctx.moveTo(w / 6, -d / 2 + backD);
    ctx.lineTo(w / 6, d / 2);
    ctx.stroke();
  }
  // Car — top-down: sedan body with cabin, windshield lines and wheels
  else if (type === 'car') {
    const len = 4.4 * zoom;   // along +X
    const wid = 1.8 * zoom;
    const r = 0.3 * zoom;     // body corner radius

    // Wheels (peek out from under the body)
    const wheelL = 0.6 * zoom;
    const wheelW = 0.2 * zoom;
    ctx.fillStyle = CAR.tire;
    for (const [wx, wy] of [
      [-len / 2 + 0.7 * zoom, -wid / 2 - wheelW * 0.25],
      [ len / 2 - 1.3 * zoom, -wid / 2 - wheelW * 0.25],
      [-len / 2 + 0.7 * zoom,  wid / 2 - wheelW * 0.75],
      [ len / 2 - 1.3 * zoom,  wid / 2 - wheelW * 0.75],
    ]) {
      ctx.fillRect(wx, wy, wheelL, wheelW);
    }

    // Body
    ctx.fillStyle = CAR.body;
    ctx.beginPath();
    ctx.roundRect(-len / 2, -wid / 2, len, wid, r);
    ctx.fill();
    ctx.strokeStyle = CAR.outline;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Cabin / roof
    const cabinLen = len * 0.42;
    const cabinW = wid * 0.78;
    ctx.fillStyle = CAR.roof;
    ctx.beginPath();
    ctx.roundRect(-cabinLen / 2 + len * 0.02, -cabinW / 2, cabinLen, cabinW, r * 0.6);
    ctx.fill();

    // Windshield + rear window bands
    ctx.fillStyle = CAR.glass;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(cabinLen / 2 + len * 0.02, -cabinW / 2 + 2, 0.25 * zoom, cabinW - 4);
    ctx.fillRect(-cabinLen / 2 + len * 0.02 - 0.22 * zoom, -cabinW / 2 + 2, 0.2 * zoom, cabinW - 4);
    ctx.globalAlpha = 1;
  }
  // Cones — top-down: small filled circle with a center dot (tip visible)
  else if (type === 'cone') {
    const radius = (0.36 * zoom) / 2; // ~14" traffic cone base
    ctx.fillStyle = CONE_COLORS.body;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = CONE_COLORS.outline;
    ctx.lineWidth = 1;
    ctx.stroke();
    // Inner stripe ring
    ctx.strokeStyle = CONE_COLORS.stripe;
    ctx.lineWidth = Math.max(1, radius * 0.15);
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.55, 0, Math.PI * 2);
    ctx.stroke();
    // Tip dot
    ctx.fillStyle = CONE_COLORS.outline;
    ctx.beginPath();
    ctx.arc(0, 0, Math.max(1, radius * 0.18), 0, Math.PI * 2);
    ctx.fill();
  }
  // Fault lines — top-down: thin red rectangle
  else if (type === 'fault_line') {
    const width = 0.05 * zoom;
    const height = 1 * zoom;

    ctx.fillStyle = FAULT_LINE.fill;
    ctx.fillRect(-width / 2, -height / 2, width, height);
  }
  // Hard cover — thin opaque panel (blocks LOS). Depth is the panel thickness.
  else if (type === 'hard_cover') {
    const width = 1 * zoom;
    const depth = Math.max(3, 0.04 * zoom);

    ctx.fillStyle = HARD_COVER.fill;
    ctx.fillRect(-width / 2, -depth / 2, width, depth);
    ctx.strokeStyle = HARD_COVER.outline;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(-width / 2, -depth / 2, width, depth);

    // Short perpendicular ticks along the panel signal "solid"
    ctx.strokeStyle = HARD_COVER.outline;
    ctx.lineWidth = 1;
    const step = Math.max(6, 0.15 * zoom);
    const tick = Math.max(3, depth * 0.9);
    ctx.beginPath();
    for (let x = -width / 2 + step; x < width / 2; x += step) {
      ctx.moveTo(x, -tick / 2);
      ctx.lineTo(x, tick / 2);
    }
    ctx.stroke();
  }
  // Soft cover — thin see-through panel (doesn't stop bullets). The
  // with_window variant adds a 40 x 40 cm opaque red panel in the
  // centre, drawn here as a small solid red bar that breaks the dashed
  // see-through outline so the variant is identifiable from top-down.
  else if (type === 'soft_cover') {
    const width = 1 * zoom;
    const depth = Math.max(3, 0.04 * zoom);

    ctx.fillStyle = SOFT_COVER.fill;
    ctx.globalAlpha = 0.35;
    ctx.fillRect(-width / 2, -depth / 2, width, depth);
    ctx.globalAlpha = 1;

    ctx.strokeStyle = SOFT_COVER.outline;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(-width / 2, -depth / 2, width, depth);
    ctx.setLineDash([]);

    if (obj.variant === 'with_window') {
      const winW = Math.min(width * 0.4, 0.4 * zoom);
      const winD = Math.max(depth, 4);
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(-winW / 2, -winD / 2, winW, winD);
      ctx.strokeStyle = '#7f1d1d';
      ctx.lineWidth = 1;
      ctx.strokeRect(-winW / 2, -winD / 2, winW, winD);
    }
    if (obj.variant === 'prone') {
      // Two small dots at the leg positions to hint at the elevated stand.
      ctx.fillStyle = SOFT_COVER.outline;
      const dotR = Math.max(2, depth * 0.6);
      ctx.beginPath();
      ctx.arc(-width / 2, 0, dotR, 0, Math.PI * 2);
      ctx.arc( width / 2, 0, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  // Steel plate on a stand — round head with a short rectangular stem
  else if (type === 'plate') {
    const inchesToMeters = 0.0254;
    const radius = (8 * inchesToMeters * zoom) / 2;
    const stemW = radius * 0.4;
    const stemH = radius * 1.2;

    ctx.fillStyle = PLATE.stem;
    ctx.fillRect(-stemW / 2, 0, stemW, stemH);
    ctx.fillStyle = PLATE.plate;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = PLATE.outline;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  // Swinger — circle with swing arc arrows
  else if (type === 'swinger') {
    const inchesToMeters = 0.0254;
    const radius = (8 * inchesToMeters * zoom) / 2;
    const armLen = radius * 2.5;

    // Pivot + arm
    ctx.strokeStyle = MOVING.armDark;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, -armLen);
    ctx.lineTo(0, 0);
    ctx.stroke();

    // Arc showing swing path
    ctx.strokeStyle = MOVING.armLight;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.arc(0, -armLen, armLen, Math.PI * 0.25, Math.PI * 0.75, false);
    ctx.stroke();
    ctx.setLineDash([]);

    // Target head
    ctx.fillStyle = MOVING.body;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = MOVING.outline;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  // Drop turner — square plate with "turn" arrow
  else if (type === 'drop_turner') {
    const inchesToMeters = 0.0254;
    const side = 8 * inchesToMeters * zoom;

    ctx.fillStyle = MOVING.body;
    ctx.fillRect(-side / 2, -side / 2, side, side);
    ctx.strokeStyle = MOVING.outline;
    ctx.lineWidth = 1;
    ctx.strokeRect(-side / 2, -side / 2, side, side);

    // Rotation arrow
    ctx.strokeStyle = MOVING.arrow;
    ctx.lineWidth = Math.max(1, side * 0.08);
    ctx.beginPath();
    ctx.arc(0, 0, side * 0.3, -Math.PI * 0.75, Math.PI * 0.5, false);
    ctx.stroke();
  }
  // Texas star — 5-point star
  else if (type === 'texas_star') {
    const outer = 0.35 * zoom;
    const inner = outer * 0.45;
    ctx.fillStyle = MOVING.body;
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = MOVING.outline;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  // Shooter — blueprint marker: readable in construction PDFs, with pose hints.
  else if (type === 'shooter') {
    const variant = obj.variant || 'shooter_relaxed';
    const markerR = 0.32 * zoom;
    const headR = 0.10 * zoom;

    // Optional pose props behind the marker.
    if (variant === 'shooter_seated') {
      const cw = 0.56 * zoom;
      const cd = 0.42 * zoom;
      ctx.fillStyle = SHOOTER.chairWood;
      ctx.fillRect(-cw / 2, markerR * 0.20, cw, cd);
      ctx.strokeStyle = SHOOTER.chairDark;
      ctx.lineWidth = 1;
      ctx.strokeRect(-cw / 2, markerR * 0.20, cw, cd);
      ctx.fillStyle = SHOOTER.chairDark;
      ctx.fillRect(-cw / 2, markerR * 0.20 + cd * 0.68, cw, cd * 0.14);
    }

    if (variant === 'shooter_broom') {
      const bl = 0.88 * zoom;
      const bristleW = 0.25 * zoom;
      const bristleH = 0.06 * zoom;

      ctx.strokeStyle = SHOOTER.broomStick;
      ctx.lineWidth = Math.max(2, 0.03 * zoom);
      ctx.beginPath();
      ctx.moveTo(0, -markerR * 0.35);
      ctx.lineTo(0, -bl);
      ctx.stroke();
      ctx.fillStyle = SHOOTER.broomBristle;
      ctx.fillRect(-bristleW / 2, -bl - bristleH, bristleW, bristleH);
      ctx.strokeStyle = SHOOTER.broomOutline;
      ctx.lineWidth = 1;
      ctx.strokeRect(-bristleW / 2, -bl - bristleH, bristleW, bristleH);
    }

    // Outer construction marker.
    ctx.fillStyle = SHOOTER.fill;
    ctx.strokeStyle = SHOOTER.outline;
    ctx.lineWidth = Math.max(2, 0.035 * zoom);
    ctx.beginPath();
    ctx.arc(0, 0, markerR, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Facing arrow: the object rotation already orients this local -Y marker.
    ctx.fillStyle = SHOOTER.outline;
    ctx.beginPath();
    ctx.moveTo(0, -markerR - 0.22 * zoom);
    ctx.lineTo(0.12 * zoom, -markerR + 0.03 * zoom);
    ctx.lineTo(-0.12 * zoom, -markerR + 0.03 * zoom);
    ctx.closePath();
    ctx.fill();

    // Simple person glyph inside the marker.
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = SHOOTER.outline;
    ctx.lineWidth = Math.max(2, 0.03 * zoom);
    ctx.beginPath();
    ctx.arc(0, -0.06 * zoom, headR, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-0.18 * zoom, 0.15 * zoom);
    ctx.quadraticCurveTo(0, 0.02 * zoom, 0.18 * zoom, 0.15 * zoom);
    ctx.stroke();

    if (variant === 'shooter_hands_up') {
      ctx.beginPath();
      ctx.moveTo(-0.17 * zoom, 0.03 * zoom);
      ctx.lineTo(-0.26 * zoom, -0.19 * zoom);
      ctx.moveTo(0.17 * zoom, 0.03 * zoom);
      ctx.lineTo(0.26 * zoom, -0.19 * zoom);
      ctx.stroke();
    }

    const badge =
      variant === 'shooter_hands_up' ? 'U' :
      variant === 'shooter_seated'   ? 'S' :
      variant === 'shooter_broom'    ? 'B' : 'R';
    const badgeW = 0.32 * zoom;
    const badgeH = 0.18 * zoom;
    ctx.fillStyle = SHOOTER.outline;
    ctx.beginPath();
    ctx.roundRect(-badgeW / 2, markerR * 0.48, badgeW, badgeH, badgeH / 2);
    ctx.fill();
    ctx.fillStyle = SHOOTER.text;
    ctx.font = `bold ${Math.max(8, badgeH * 0.72)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(badge, 0, markerR * 0.48 + badgeH / 2);
  }
  // IDPA paper swinger / one-time swinger / popup — share the tan silhouette
  // with a small movement glyph to indicate the type. Swinger variants also
  // draw a base circle underneath to match the "target on a stand" form.
  else if (
    type === 'paper_swinger' ||
    type === 'paper_swinger_once' ||
    type === 'paper_popup'
  ) {
    // Shared IDPA silhouette footprint (idpa variant by default)
    const bw = 0.432 * zoom;
    const bh = 0.610 * zoom;
    const hdw = 0.108 * zoom;
    const hdh = 0.178 * zoom;
    const c  = 0.076 * zoom;
    const totalH = bh + hdh;
    const hw = bw / 2;
    const hhw = hdw / 2;
    const top = -totalH / 2;
    const headBot = top + hdh;
    const bot = totalH / 2;

    // Stand base disc shown under swingers so the top-down footprint reads
    // as a freestanding rocker rig, not a floating target.
    if (type === 'paper_swinger' || type === 'paper_swinger_once') {
      ctx.fillStyle = MOVING.armDark;
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.arc(0, bot - 0.02 * zoom, bw * 0.75, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = MOVING.outline;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    ctx.fillStyle = PAPER.body;
    ctx.beginPath();
    ctx.moveTo(-hw + c, bot);
    ctx.lineTo( hw - c, bot);
    ctx.lineTo( hw, bot - c);
    ctx.lineTo( hw, headBot + c);
    ctx.lineTo( hw - c, headBot);
    ctx.lineTo( hhw, headBot);
    ctx.lineTo( hhw, top);
    ctx.lineTo(-hhw, top);
    ctx.lineTo(-hhw, headBot);
    ctx.lineTo(-hw + c, headBot);
    ctx.lineTo(-hw, headBot + c);
    ctx.lineTo(-hw, bot - c);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = PAPER.outline;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Movement glyph on top of the head
    ctx.strokeStyle = MOVING.armDark;
    ctx.lineWidth = Math.max(2, 0.03 * zoom);
    if (type === 'paper_swinger') {
      // Double-sided arc (continuous swing)
      const arcR = bw * 0.6;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(0, top - arcR * 0.3, arcR, Math.PI * 0.25, Math.PI * 0.75, false);
      ctx.stroke();
      ctx.setLineDash([]);
    } else if (type === 'paper_swinger_once') {
      // One-shot arrow: curved path with arrowhead
      const arcR = bw * 0.6;
      ctx.beginPath();
      ctx.arc(0, top - arcR * 0.3, arcR, Math.PI * 0.5, Math.PI * 0.15, true);
      ctx.stroke();
      const ax = arcR * Math.cos(Math.PI * 0.15);
      const ay = top - arcR * 0.3 + arcR * Math.sin(Math.PI * 0.15);
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(ax - 0.08 * zoom, ay - 0.04 * zoom);
      ctx.moveTo(ax, ay);
      ctx.lineTo(ax - 0.03 * zoom, ay + 0.07 * zoom);
      ctx.stroke();
    } else if (type === 'paper_popup') {
      // Up / down arrows
      const arr = bw * 0.4;
      const ay = top - arr * 0.5;
      ctx.beginPath();
      ctx.moveTo(0, ay - arr / 2);
      ctx.lineTo(0, ay + arr / 2);
      ctx.moveTo(-arr * 0.25, ay - arr / 2 + arr * 0.2);
      ctx.lineTo(0, ay - arr / 2);
      ctx.lineTo(arr * 0.25, ay - arr / 2 + arr * 0.2);
      ctx.moveTo(-arr * 0.25, ay + arr / 2 - arr * 0.2);
      ctx.lineTo(0, ay + arr / 2);
      ctx.lineTo(arr * 0.25, ay + arr / 2 - arr * 0.2);
      ctx.stroke();
    }
  }
  // Bobber — circle with vertical up/down arrows
  else if (type === 'bobber') {
    const inchesToMeters = 0.0254;
    const radius = (8 * inchesToMeters * zoom) / 2;
    ctx.fillStyle = MOVING.body;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = MOVING.outline;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Up/down arrows
    ctx.strokeStyle = MOVING.arrow;
    ctx.lineWidth = Math.max(1, radius * 0.15);
    const arr = radius * 0.6;
    ctx.beginPath();
    ctx.moveTo(0, -arr);
    ctx.lineTo(0,  arr);
    ctx.moveTo(-arr * 0.4, -arr + arr * 0.35);
    ctx.lineTo(0, -arr);
    ctx.lineTo( arr * 0.4, -arr + arr * 0.35);
    ctx.moveTo(-arr * 0.4,  arr - arr * 0.35);
    ctx.lineTo(0,  arr);
    ctx.lineTo( arr * 0.4,  arr - arr * 0.35);
    ctx.stroke();
  }
  // Fallback — generic circle
  else {
    const inchesToMeters = 0.0254;
    const radius = (8 * inchesToMeters * zoom) / 2;

    ctx.fillStyle = FALLBACK.fill;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = FALLBACK.outline;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  ctx.restore();

  // Draw label above object
  if (obj.label) {
    ctx.save();
    const labelScreenPos = worldToScreen(
      { x: obj.position.x, y: obj.position.y },
      zoom,
      panOffset
    );
    ctx.font = `bold ${Math.max(10, 12 * Math.min(zoom / 40, 1))}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = LABEL.fill;
    ctx.strokeStyle = LABEL.outline;
    ctx.lineWidth = 3;
    ctx.strokeText(obj.label, labelScreenPos.x, labelScreenPos.y - 0.35 * zoom);
    ctx.fillText(obj.label, labelScreenPos.x, labelScreenPos.y - 0.35 * zoom);
    ctx.restore();
  }

  // Draw selection bounding box
  if (isSelected) {
    ctx.save();

    const screenPos2 = worldToScreen(
      { x: obj.position.x, y: obj.position.y },
      zoom,
      panOffset
    );

    // Simple bounding box (could be improved with rotation)
    const size = 0.5 * zoom;
    ctx.strokeStyle = CHROME.selected;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(
      screenPos2.x - size / 2,
      screenPos2.y - size / 2,
      size,
      size
    );

    // Draw corner handles
    ctx.fillStyle = CHROME.selected;
    const handleSize = 4;
    const corners = [
      { x: -size / 2, y: -size / 2 },
      { x: size / 2, y: -size / 2 },
      { x: size / 2, y: size / 2 },
      { x: -size / 2, y: size / 2 },
    ];

    for (const corner of corners) {
      ctx.fillRect(
        screenPos2.x + corner.x - handleSize / 2,
        screenPos2.y + corner.y - handleSize / 2,
        handleSize,
        handleSize
      );
    }

    ctx.restore();
  }
}

/**
 * Render an annotation
 */
export function renderAnnotation(
  ctx: CanvasRenderingContext2D,
  ann: Annotation,
  zoom: number,
  panOffset: Vec2
): void {
  ctx.save();

  const screenPos = worldToScreen(
    { x: ann.position.x, y: ann.position.y },
    zoom,
    panOffset
  );

  if (ann.type === 'text') {
    ctx.fillStyle = ANNOTATION.fill;
    ctx.font = `${ann.font_size || 14}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    if (ann.rotation) {
      ctx.translate(screenPos.x, screenPos.y);
      ctx.rotate((ann.rotation * Math.PI) / 180);
      ctx.fillText(ann.text || '', 0, 0);
    } else {
      ctx.fillText(ann.text || '', screenPos.x, screenPos.y);
    }
  } else if (ann.type === 'measurement' && ann.points && ann.points.length >= 2) {
    const startScreen = worldToScreen(
      { x: ann.points[0].x, y: ann.points[0].y },
      zoom,
      panOffset
    );
    const endScreen = worldToScreen(
      { x: ann.points[1].x, y: ann.points[1].y },
      zoom,
      panOffset
    );

    // Draw line
    ctx.strokeStyle = ANNOTATION.fill;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(startScreen.x, startScreen.y);
    ctx.lineTo(endScreen.x, endScreen.y);
    ctx.stroke();

    // Draw measurement text
    const dx = ann.points[1].x - ann.points[0].x;
    const dy = ann.points[1].y - ann.points[0].y;
    const distance = Math.sqrt(dx * dx + dy * dy).toFixed(2);

    const midX = (startScreen.x + endScreen.x) / 2;
    const midY = (startScreen.y + endScreen.y) / 2;

    ctx.fillStyle = ANNOTATION.fill;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${distance}m`, midX, midY - 10);
  }

  ctx.restore();
}

/**
 * Draw alignment guides (dashed lines) while dragging an object. `axes`
 * is a list of world-space x and/or y values that the dragged object is
 * currently aligned with — typically the centers of other objects nearby.
 * Lines extend across the full viewport so the user can see what they're
 * aligning to.
 */
export function renderAlignmentGuides(
  ctx: CanvasRenderingContext2D,
  xAxes: number[],
  yAxes: number[],
  zoom: number,
  panOffset: Vec2,
  viewWidth: number,
  viewHeight: number,
): void {
  if (xAxes.length === 0 && yAxes.length === 0) return;
  ctx.save();
  ctx.strokeStyle = CHROME.alignmentGuide;
  ctx.lineWidth = 1;
  ctx.setLineDash([6, 4]);

  for (const x of xAxes) {
    const screenX = x * zoom + panOffset.x;
    ctx.beginPath();
    ctx.moveTo(screenX, 0);
    ctx.lineTo(screenX, viewHeight);
    ctx.stroke();
  }
  for (const y of yAxes) {
    const screenY = y * zoom + panOffset.y;
    ctx.beginPath();
    ctx.moveTo(0, screenY);
    ctx.lineTo(viewWidth, screenY);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();
}

/**
 * Render a selection box (drag select rectangle)
 */
export function renderSelectionBox(
  ctx: CanvasRenderingContext2D,
  start: Vec2,
  end: Vec2
): void {
  ctx.save();

  const topLeft = {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
  };
  const bottomRight = {
    x: Math.max(start.x, end.x),
    y: Math.max(start.y, end.y),
  };

  const width = bottomRight.x - topLeft.x;
  const height = bottomRight.y - topLeft.y;

  ctx.fillStyle = CHROME.selected;
  ctx.globalAlpha = 0.1;
  ctx.fillRect(topLeft.x, topLeft.y, width, height);

  ctx.globalAlpha = 1.0;
  ctx.strokeStyle = CHROME.selected;
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 4]);
  ctx.strokeRect(topLeft.x, topLeft.y, width, height);

  ctx.restore();
}

/**
 * Render a placement preview (preview of object being placed)
 */
export function renderPlacementPreview(
  ctx: CanvasRenderingContext2D,
  type: string,
  variant: string | null,
  worldPos: Vec2,
  zoom: number,
  panOffset: Vec2
): void {
  ctx.save();
  ctx.globalAlpha = 0.5;

  // Create a temporary object for rendering
  const tempObj: StageObject = {
    id: 'preview',
    type: type as StageObjectType,
    variant: variant ? (variant as ObjectVariant) : undefined,
    position: { x: worldPos.x, y: worldPos.y, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    on_stand: false,
    stand_height_m: 0,
    locked: false,
    facing_direction: 0,
  };

  renderObject(ctx, tempObj, zoom, panOffset, false, false);

  ctx.restore();
}

/**
 * Clear the canvas with a background color
 */
export function clearCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  backgroundColor: string = '#ffffff'
): void {
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, width, height);
}
